import React from 'react';

import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProviderOrder } from '../../../../models/provider.order.model';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { Expense } from '../../../../models/expense.model';
import { TaxDetail } from '../../../../models/tax.detail.model';
import { formatMoney, formatTaxRate } from '../../../../scripts/format.value.script';
import { ForeignCurrency } from '../../../../models/currency.model';
import { dateFormat } from '../../../../scripts/date.time.format.script';

export interface AppPurchaseViewAmountProps {
  purchase: string,
  record: any | ProviderOrder | ProviderInvoice | Expense
};

const AppPurchaseViewAmount: React.FunctionComponent<AppPurchaseViewAmountProps> = ({purchase, record}) => {
  const {lang} = StateLanguage()
  const fc: ForeignCurrency | null = record.foreign_currency ?? null

  // For purchases: primary display = foreign currency (EUR) if present, CHF otherwise
  const primaryCode = fc ? (fc.code ?? '') : (record.currency?.code ?? '')
  const primaryAmount = fc ? fc.amount : record.amount

  return (
    <div className="card card-flush mb-5">
      <div className="card-header">
        <div className="card-title">
          <h4>{lang.labels.amount}</h4>
        </div>
      </div>
      <div className="card-body pt-0">
        <div className="d-flex justify-content-end">
          <div className="mw-500px">
            <div className="d-flex flex-stack px-5 py-2 bg-light-info border-top border-bottom">
              <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.subTotal}:</div>
              <div className="text-end fw-bolder ps-20">
                {primaryCode} {formatMoney(primaryAmount?.sub_total ?? 0)}
              </div>
            </div>
            <div className="d-flex flex-stack px-5 py-2 bg-light-info border-bottom">
              <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.taxTotal}:</div>
              <div className="text-end fw-bolder ps-20">
                {primaryCode} {formatMoney(primaryAmount?.tax_total ?? 0)}
              </div>
            </div>
            { (record.amount?.tax_list ?? []).map (( (item: TaxDetail, index: number) => { return (
              <div  key={index} className="d-flex flex-stack ps-10 pe-5 py-1 bg-light-dark">
                <div className="fw-bold pe-20 text-secondary fs-8">{lang.labels.tax}: {formatTaxRate(item.tax_rate)} %</div>
                <div className="text-end fw-bolder ps-20 fs-7">{primaryCode} {formatMoney(item.tax_value)}</div>
              </div>
            )}))}
            <div className="d-flex flex-stack px-5 py-2 bg-light-warning border-top border-bottom">
              <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.total}:</div>
              <div className="text-end fw-bolder ps-20">
                {primaryCode} {formatMoney(primaryAmount?.total ?? 0)}
              </div>
            </div>
            { fc &&
              <div className="d-flex flex-stack px-5 py-2 bg-light-warning border-bottom">
                <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.total} ({record.currency?.code ?? ''}):</div>
                <div className="text-end fw-bolder ps-20">
                  {record.currency?.code ?? ''} {formatMoney(record.amount?.total ?? 0)}
                </div>
              </div>
            }
            { purchase === "invoice" &&
              <>
                <div className="d-flex flex-stack px-5 py-2 bg-light-success border-top border-bottom">
                  <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.payed}:</div>
                  <div className="text-end fw-bolder ps-20">{record.currency?.code ?? ''} {formatMoney(record.amount?.paid ?? 0)}</div>
                </div>
                <div className="d-flex flex-stack px-5 py-2 bg-light-danger border-top border-bottom">
                  <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.debt}:</div>
                  <div className="text-end fw-bolder ps-20">{record.currency?.code ?? ''} {formatMoney(record.amount?.debt ?? 0)}</div>
                </div>
              </>
            }
            { fc &&
              <div className="d-flex flex-stack px-5 py-2 bg-light border-bottom">
                <div className="fw-bold text-secondary fs-8">{lang.labels.rateInfo}:</div>
                <div className="text-end fw-bold text-muted fs-8">1 {fc.code ?? ''} = {fc.exchange_rate > 0 ? formatMoney(1 / fc.exchange_rate) : '0.00'} {record.currency?.code ?? ''} ({fc.exchange_date ? dateFormat(lang.code, fc.exchange_date) : ''})</div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppPurchaseViewAmount;
